import { useEffect, useRef, useState } from 'react';

/**
 * Check if an element is out of the viewport
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {Node} elem The element to check
 * @return {Object} A set of booleans for each side of the element
 */
const isOutOfViewport = (elem: any) => {
  // Get element's bounding
  const bounding = elem.getBoundingClientRect();

  // Check if it's out of the viewport on each side
  const out: any = {};
  out.top = bounding.top < 0;
  out.left = bounding.left < 0;
  out.bottom = bounding.bottom > (window.innerHeight || document.documentElement.clientHeight);
  out.right = bounding.right > (window.innerWidth || document.documentElement.clientWidth);
  out.any = out.top || out.left || out.bottom || out.right;
  out.all = out.top && out.left && out.bottom && out.right;

  return { intersections: out, bounding };
};

export function useOnClickOutside<T>(initialState: T) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<T | any>(initialState);
  const [bounding, setBounding] = useState({});

  const handleOutsideClick = (event: any) => {
    if (ref.current && !ref.current.contains(event.target)) setVisible(false);
  };

  useEffect(() => {
    setBounding(isOutOfViewport(ref?.current));
  }, [visible, ref]);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);
    return () => document.removeEventListener('click', handleOutsideClick, true);
  }, [ref]);

  return {
    visible,
    setVisible,
    ref,
    bounding,
  };
}

export default useOnClickOutside;
